import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { CATEGORY_LABELS } from './operationStates';

/*
 * TaskComposer — add / edit a manual task. First-class host CRUD: title,
 * notes, category, urgency, due date, waiting-on, and (when editing) state.
 * Submits the plain field payload accepted by createOperation / updateOperation.
 */

const URGENCIES = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'when_possible', label: 'When possible' },
];

const STATES = [
  { value: 'active', label: 'Active — your move' },
  { value: 'pending_decision', label: 'Needs a decision' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'waiting', label: 'Waiting on someone' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'snoozed', label: 'Snoozed' },
];

function blank() {
  return {
    title: '', detail: '', category: 'other', urgency: 'this_week',
    due_date: '', waiting_on: '', state: 'active', add_to_calendar: true,
  };
}

export default function TaskComposer({ open, mode = 'add', op = null, busy = false, onClose, onSubmit }) {
  const [form, setForm] = useState(blank());

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && op) {
      setForm({
        title: op.title || '',
        detail: op.detail || '',
        category: op.category || 'other',
        urgency: op.urgency || 'this_week',
        due_date: op.due_date ? String(op.due_date).slice(0, 10) : '',
        waiting_on: op.waiting_on || '',
        state: op.state === 'completed' ? 'active' : (op.state || 'active'),
      });
    } else {
      setForm(blank());
    }
  }, [open, mode, op]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) return;
    const payload = {
      title,
      detail: form.detail.trim() || null,
      category: form.category,
      urgency: form.urgency,
      due_date: form.due_date || null,
      waiting_on: form.waiting_on.trim() || null,
      // When adding with a due date, mirror it onto the Calendar (opt-in).
      // The parent reads this flag and strips it before saving the task.
      add_to_calendar: mode === 'add' && !!form.due_date && form.add_to_calendar,
    };
    if (mode === 'edit') payload.state = form.state;
    else if (form.state && form.state !== 'active') payload.state = form.state;
    await onSubmit(payload);
  };

  return (
    <div className="ero-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <form className="ero-modal" onSubmit={submit} data-testid="task-composer">
        <button type="button" className="ero-modal-close" onClick={onClose} aria-label="Close">
          <X size={16} aria-hidden="true" />
        </button>
        <h3>{mode === 'edit' ? 'Edit task' : 'Add a task'}</h3>
        <p className="sub">
          {mode === 'edit'
            ? 'Update any detail — this is your task to shape.'
            : 'Anything from “custom napkin rings” to “hire a saxophonist.” It’s yours, tracked alongside everything else.'}
        </p>

        <div className="ero-field">
          <label htmlFor="ero-title">Task</label>
          <input id="ero-title" type="text" value={form.title} onChange={set('title')}
                 placeholder="What needs doing?" autoFocus />
        </div>

        <div className="ero-field">
          <label htmlFor="ero-detail">Notes</label>
          <textarea id="ero-detail" value={form.detail} onChange={set('detail')}
                    placeholder="Any context, amounts, links, or reminders…" />
        </div>

        <div className="ero-field-row">
          <div className="ero-field">
            <label htmlFor="ero-cat">Category</label>
            <select id="ero-cat" value={form.category} onChange={set('category')}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div className="ero-field">
            <label htmlFor="ero-urg">Priority</label>
            <select id="ero-urg" value={form.urgency} onChange={set('urgency')}>
              {URGENCIES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </div>
        </div>

        <div className="ero-field-row">
          <div className="ero-field">
            <label htmlFor="ero-due">Due date</label>
            <input id="ero-due" type="date" value={form.due_date} onChange={set('due_date')} />
          </div>
          <div className="ero-field">
            <label htmlFor="ero-state">{mode === 'edit' ? 'State' : 'Starts as'}</label>
            <select id="ero-state" value={form.state} onChange={set('state')}>
              {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {mode === 'add' && form.due_date && (
          <label className="ero-cal-opt">
            <input type="checkbox" checked={form.add_to_calendar}
                   onChange={(e) => setForm((f) => ({ ...f, add_to_calendar: e.target.checked }))} />
            <span>Add this to your Calendar on <b>{form.due_date}</b> — your notes carry over as the description.</span>
          </label>
        )}

        {(form.state === 'waiting' || form.state === 'delegated') && (
          <div className="ero-field">
            <label htmlFor="ero-waiting">Waiting on</label>
            <input id="ero-waiting" type="text" value={form.waiting_on} onChange={set('waiting_on')}
                   placeholder="Who are you waiting on?" />
          </div>
        )}

        <div className="ero-modal-actions">
          <button type="button" className="ero-btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="ero-btn primary" disabled={busy || !form.title.trim()}>
            {busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add task'}
          </button>
        </div>
      </form>
    </div>
  );
}
